import { useEffect, useState } from "react";
import { AnimatePresence, m, useMotionValueEvent, useScroll } from "motion/react";
import logo72 from "../../assets/images/logo-72.webp";
import logo108 from "../../assets/images/logo-108.webp";
import { routes, socials } from "../shared/links";
import { useReady } from "../../boot";

const Navbar = () => {
	const { scrollY } = useScroll();
	const [scrolled, setScrolled] = useState(false);
	const [open, setOpen] = useState(false);
	const ready = useReady();

	useMotionValueEvent(scrollY, "change", (y) => setScrolled(y > 40));

	useEffect(() => {
		document.body.style.overflow = open ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	return (
		<>
			<m.header
				initial={{ y: -40, opacity: 0 }}
				animate={ready ? { y: 0, opacity: 1 } : undefined}
				transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
				className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4"
			>
				<nav
					className={`flex w-full max-w-5xl items-center justify-between rounded-full border py-2 pl-3 pr-2 transition-[background-color,border-color,box-shadow] duration-500 ${
						scrolled
							? // No backdrop blur on phones: re-blurring the animated background every frame is costly there.
								"border-white/10 bg-ink/90 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.8)] md:bg-ink/70 md:backdrop-blur-xl"
							: "border-transparent bg-transparent"
					}`}
				>
					<a href="#top" className="flex items-center gap-2.5" aria-label="Back to top">
						<img
							src={logo72}
							srcSet={`${logo72} 2x, ${logo108} 3x`}
							alt="محمد شريف"
							width="79"
							height="36"
							className="h-9 w-auto"
						/>
						<span className="hidden font-display text-[15px] font-semibold tracking-tight text-parchment sm:inline">
							Mohammad Chreif
						</span>
					</a>

					<ul className="hidden items-center md:flex">
						{routes.map((route) => (
							<li key={route.href}>
								<a
									href={route.href}
									className="block rounded-full px-4 py-2 text-sm font-medium text-parchment/75 transition-colors duration-300 hover:bg-white/[0.07] hover:text-parchment"
								>
									{route.title}
								</a>
							</li>
						))}
					</ul>

					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setOpen((v) => !v)}
							className="relative z-[60] flex h-10 w-10 flex-col items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] md:hidden"
							aria-label={open ? "Close menu" : "Open menu"}
							aria-expanded={open}
						>
							<m.span
								className="block h-[1.5px] w-4 rounded bg-parchment"
								animate={open ? { rotate: 45, y: 3.75 } : { rotate: 0, y: 0 }}
							/>
							<m.span
								className="block h-[1.5px] w-4 rounded bg-parchment"
								animate={open ? { rotate: -45, y: -3.75 } : { rotate: 0, y: 0 }}
							/>
						</button>
					</div>
				</nav>
			</m.header>

			<AnimatePresence>
				{open && (
					<m.div
						className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/[0.97] px-8 md:hidden"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.35 }}
					>
						<ul className="space-y-2">
							{routes.map((route, i) => (
								<m.li
									key={route.href}
									initial={{ opacity: 0, y: 24 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
								>
									<a
										href={route.href}
										onClick={() => setOpen(false)}
										className="block py-2 font-display text-5xl font-semibold tracking-tight text-parchment"
									>
										{route.title}
									</a>
								</m.li>
							))}
						</ul>
						<m.div
							className="mt-12 flex flex-col gap-6"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.4 }}
						>
							<div className="flex gap-3">
								{socials.map(({ label, href, Icon }) => (
									<a
										key={label}
										href={href}
										target="_blank"
										rel="noreferrer"
										aria-label={label}
										className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-parchment"
									>
										<Icon className="h-5 w-5" />
									</a>
								))}
							</div>
						</m.div>
					</m.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default Navbar;
